import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSearch } from '@fortawesome/free-solid-svg-icons';
import { SCREENS } from '../responsive';

type SearchNavSummaryProps = {
  locationName: string;
  startDate?: Date | string | null;
  startTime?: string | null;
  returnDate?: Date | string | null;
  returnTime?: string | null;
  driverAge?: number | null;
  showFilters?: boolean;
  onEdit: () => void;
  onOpenFilters?: () => void;
};

function formatClock(time: string) {
  const match = time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
  if (!match) return time;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const ampmRaw = match[3];
  if (ampmRaw) {
    const ap = ampmRaw.toUpperCase();
    if (ap === 'PM' && hour !== 12) hour += 12;
    if (ap === 'AM' && hour === 12) hour = 0;
  }
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${suffix}`;
}

function asDate(value: Date | string) {
  return typeof value === 'string' ? new Date(value) : value;
}

function formatDesktopWhen(date?: Date | string | null, time?: string | null) {
  if (!date || !time) return 'Add date';
  const parsed = asDate(date);
  if (Number.isNaN(parsed.getTime())) return 'Add date';
  const datePart = parsed.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
  return `${datePart}, ${formatClock(time)}`;
}

function formatAgeLabel(age?: number | null) {
  const value = Number(age);
  if (!Number.isFinite(value) || value >= 25) return '25+';
  if (value >= 21) return '21-24';
  return '18-20';
}

function formatMobileRange(
  startDate?: Date | string | null,
  startTime?: string | null,
  returnDate?: Date | string | null,
  returnTime?: string | null,
) {
  if (!startDate || !returnDate || !startTime || !returnTime) return 'Select dates';
  const start = asDate(startDate);
  const end = asDate(returnDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Select dates';
  const day = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${day(start)}, ${formatClock(startTime)} - ${day(end)}, ${formatClock(returnTime)}`;
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 52px;
  padding: 4px 4px 4px 14px;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);

  @media (min-width: ${SCREENS.md}) {
    height: 48px;
    padding: 4px 6px 4px 4px;
  }
`;

const MobileBody = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 8px;

  @media (min-width: ${SCREENS.md}) {
    display: none;
  }
`;

const MobileCopy = styled.div`
  min-width: 0;
  flex: 1;
`;

const MobileLocation = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MobileDates = styled.div`
  margin-top: 1px;
  display: flex;
  min-width: 0;
  font-size: 12px;
  line-height: 1.2;
  color: #6b7280;
`;

const MobileDateRange = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileAge = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
`;

const FilterButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
`;

const DesktopBody = styled.div`
  display: none;

  @media (min-width: ${SCREENS.md}) {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
  }
`;

const Segment = styled.div<{ $fixed?: boolean }>`
  flex: ${({ $fixed }) => ($fixed ? '0 0 auto' : '1')};
  min-width: 0;
  padding: 0 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
`;

const SegmentLabel = styled.span`
  font-size: 11px;
  line-height: 1.1;
  font-weight: 600;
  color: #6b7280;
`;

const SegmentValue = styled.span`
  font-size: 14px;
  line-height: 1.2;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Divider = styled.span`
  width: 1px;
  align-self: stretch;
  margin: 8px 0;
  background: #e5e7eb;
  flex-shrink: 0;
`;

const SearchIcon = styled.span`
  width: 36px;
  height: 36px;
  margin-left: 8px;
  border-radius: 999px;
  background: #111827;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export function SearchNavSummary({
  locationName,
  startDate,
  startTime,
  returnDate,
  returnTime,
  driverAge,
  showFilters = true,
  onEdit,
  onOpenFilters,
}: SearchNavSummaryProps) {
  return (
    <Bar
      data-search-nav-summary="true"
      role="button"
      tabIndex={0}
      aria-label="Edit search"
      onClick={onEdit}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onEdit();
        }
      }}
    >
      <MobileBody>
        <MobileCopy>
          <MobileLocation>{locationName}</MobileLocation>
          <MobileDates>
            <MobileDateRange>
              {formatMobileRange(startDate, startTime, returnDate, returnTime)}
            </MobileDateRange>
            <MobileAge>{` · ${formatAgeLabel(driverAge)}`}</MobileAge>
          </MobileDates>
        </MobileCopy>
        {showFilters && (
          <FilterButton
            type="button"
            aria-label="Filters"
            onClick={event => {
              event.stopPropagation();
              onOpenFilters?.();
            }}
          >
            <FontAwesomeIcon icon={faFilter} />
          </FilterButton>
        )}
      </MobileBody>
      <DesktopBody>
        <Segment>
          <SegmentLabel>Where</SegmentLabel>
          <SegmentValue>{locationName}</SegmentValue>
        </Segment>
        <Divider />
        <Segment>
          <SegmentLabel>From</SegmentLabel>
          <SegmentValue>{formatDesktopWhen(startDate, startTime)}</SegmentValue>
        </Segment>
        <Divider />
        <Segment>
          <SegmentLabel>Until</SegmentLabel>
          <SegmentValue>{formatDesktopWhen(returnDate, returnTime)}</SegmentValue>
        </Segment>
        <Divider />
        <Segment $fixed>
          <SegmentLabel>Age</SegmentLabel>
          <SegmentValue>{formatAgeLabel(driverAge)}</SegmentValue>
        </Segment>
        <SearchIcon aria-hidden>
          <FontAwesomeIcon icon={faSearch} />
        </SearchIcon>
      </DesktopBody>
    </Bar>
  );
}
